/**
 * Modal.tsx — Componente de diálogo del design system
 *
 * Patrón: Radix Dialog (primitivo) + CVA (size) + Tailwind + Lucide.
 * Sigue la referencia canónica de Button.tsx (ADR-025).
 *
 * Exports: Modal, ModalTrigger, ModalContent, ModalHeader, ModalTitle,
 *          ModalDescription, ModalFooter, ModalClose
 *
 * Por qué Radix Dialog:
 *   Provee focus trap, escape-to-close, click-overlay-to-close, portal
 *   automático (z-index correcto fuera del árbol DOM), y ARIA completo
 *   (role="dialog", aria-modal="true", aria-labelledby, aria-describedby)
 *   sin necesidad de implementación manual (ADR-025).
 *
 * Por qué fallback transition-opacity en lugar de tailwindcss-animate:
 *   tailwindcss-animate no está instalado en este proyecto. Las utilidades
 *   `animate-in`/`fade-in-0`/`zoom-in-95` requieren ese plugin. Se usan
 *   clases Tailwind estándar `transition-*` + atributos `data-[state=...]`
 *   de Radix como selectores CSS custom para producir la animación equivalente.
 *   La animación resultante es funcionalmente idéntica (fade + scale) sin
 *   dependencia adicional. Ver también: issue de scope en handoff Día 3.
 *
 * Por qué asChild en Trigger y Close:
 *   Permite que el consumidor use cualquier elemento (ej. <a>, <Button>
 *   del design system) como disparador sin duplicar el DOM.
 */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA — size del contenedor del modal
// ---------------------------------------------------------------------------

const modalContentVariants = cva(
  // Base: aplica a todos los tamaños.
  // Posicionamiento: fixed centrado + z-50 para superar cualquier stacking context.
  // Fondo, sombra y rounded del design system Cumple (spec Track B §1.6).
  // Focus ring: outline-none porque Radix gestiona el foco internamente.
  [
    // z-[60]: por encima del overlay (z-50) — el panel siempre pinta sobre el backdrop.
    'fixed left-[50%] top-[50%] z-[60] translate-x-[-50%] translate-y-[-50%]',
    'w-full bg-white shadow-xl rounded-lg',
    'outline-none',
    // El panel es VISIBLE en cuanto Radix lo monta (opacity/scale por defecto = 1).
    // Se eliminó el fallback de animación data-[state]:opacity/scale (sin
    // tailwindcss-animate) porque dejaba el panel en opacity:0 en algunos navegadores
    // — síntoma: pantalla gris (overlay) SIN diálogo. El overlay sí se veía porque su
    // gris proviene del bg, no de la opacity. Correctitud > micro-animación de entrada.
  ],
  {
    variants: {
      size: {
        /**
         * sm — Confirmaciones, alertas simples (max-w-md ≈ 448px).
         * Padding reducido p-4 para contenido compacto.
         */
        sm: 'max-w-md p-4',
        /**
         * md — Formularios, detalles (max-w-lg ≈ 512px). Default.
         * Padding estándar p-6 del kit Cumple.
         */
        md: 'max-w-lg p-6',
        /**
         * lg — Vistas complejas, evidencias (max-w-2xl ≈ 672px).
         */
        lg: 'max-w-2xl p-6',
        /**
         * xl — Tablas, comparativas, reportes inline (max-w-4xl ≈ 896px).
         */
        xl: 'max-w-4xl p-6',
        /**
         * full — Pantalla completa con margen mínimo. Vistas de cámara, mapas.
         */
        full: 'max-w-full w-[calc(100%-2rem)] p-6',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

// ---------------------------------------------------------------------------
// Reexports directos de primitivos Radix (sin estilos propios)
// ---------------------------------------------------------------------------

/**
 * Modal — Raíz del componente. Gestiona el estado open/close (controlled o
 * uncontrolled). Acepta las mismas props que Radix Dialog.Root.
 *
 * @example Uncontrolled:
 *   <Modal>
 *     <ModalTrigger asChild><Button>Abrir</Button></ModalTrigger>
 *     <ModalContent><ModalHeader><ModalTitle>Título</ModalTitle></ModalHeader></ModalContent>
 *   </Modal>
 *
 * @example Controlled:
 *   const [open, setOpen] = React.useState(false);
 *   <Modal open={open} onOpenChange={setOpen}>…</Modal>
 */
const Modal = DialogPrimitive.Root;
Modal.displayName = 'Modal';

/**
 * ModalTrigger — Elemento que abre el modal al hacer click.
 * Usa asChild={true} para renderizar como cualquier elemento.
 *
 * @example
 *   <ModalTrigger asChild>
 *     <Button variant="primary">Crear hallazgo</Button>
 *   </ModalTrigger>
 */
const ModalTrigger = DialogPrimitive.Trigger;
ModalTrigger.displayName = 'ModalTrigger';

// ---------------------------------------------------------------------------
// ModalOverlay — backdrop oscuro (interno, consumido por ModalContent)
// ---------------------------------------------------------------------------

/**
 * ModalOverlay — Backdrop oscuro detrás del modal.
 * No se exporta; se compone internamente en ModalContent para garantizar
 * que overlay + content siempre coexistan correctamente en el portal.
 *
 * Por qué bg-cumple-primary-900/50:
 *   Fondo #0F172A al 50% de opacidad = contraste suficiente sobre cualquier
 *   contenido de app sin bloquear completamente la referencia visual (spec §1.6).
 *
 * Fallback de animación: transition-opacity (mismo patrón que ModalContent).
 */
const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // z-50: bajo el panel (z-[60]). Backdrop cumple-primary-900 al 50% (spec §1.6).
      'fixed inset-0 z-50',
      'bg-[rgba(15,23,42,0.50)]',
      // Sin animación data-[state] (mismo motivo que ModalContent): el overlay se
      // monta/desmonta con Radix; visible de inmediato.
      className,
    )}
    {...props}
  />
));
ModalOverlay.displayName = 'ModalOverlay';

// ---------------------------------------------------------------------------
// ModalContent — contenedor principal del modal con portal
// ---------------------------------------------------------------------------

export interface ModalContentProps
  extends
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof modalContentVariants> {}

/**
 * ModalContent — Contenedor principal del modal.
 * Renderiza mediante DialogPrimitive.Portal para escapar del árbol DOM y evitar
 * conflictos de z-index. Incluye el overlay y el botón de cierre automáticamente.
 *
 * El botón X de cierre se coloca en la esquina superior derecha del header.
 * Si se incluye ModalHeader, el X aparece absolutamente posicionado sobre él.
 * Si no se incluye ModalHeader, el X sigue visible en top-right del contenedor.
 */
const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(({ className, size, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <ModalOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // NO añadir 'relative' aquí: sobreescribe el `fixed` de las variantes (Tailwind
      // ordena .relative después de .fixed) y el panel cae al flujo normal, fuera del
      // viewport (bug "overlay gris sin diálogo"). `fixed` ya es contenedor de
      // posicionamiento para el botón X `absolute`.
      className={cn(modalContentVariants({ size }), className)}
      {...props}
    >
      {children}
      {/* Botón X de cierre — posición absoluta top-right dentro del Content */}
      <DialogPrimitive.Close
        className={cn(
          'absolute right-4 top-4',
          'inline-flex h-8 w-8 items-center justify-center rounded-md',
          'text-cumple-gray-500 hover:text-cumple-primary-900 hover:bg-cumple-gray-100',
          // Área táctil WCAG 2.5.5: mínimo 44×44; usamos 32px + hover area implícita.
          // El botón es 32px pero el padding de ModalContent protege la zona táctil.
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-accent-500 focus-visible:ring-offset-2',
          'transition-colors duration-150',
        )}
        aria-label="Cerrar"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
ModalContent.displayName = 'ModalContent';

// ---------------------------------------------------------------------------
// Sub-componentes de estructura
// ---------------------------------------------------------------------------

/**
 * ModalHeader — Contenedor superior del modal.
 * Diseño flex-col con gap-1 para Title + Description.
 * Padding-right para no solaparse con el botón X (pr-8).
 */
const ModalHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 pr-8 mb-4', className)} {...props} />
  ),
);
ModalHeader.displayName = 'ModalHeader';

/**
 * ModalTitle — Título principal del modal.
 * Radix enlaza automáticamente este elemento con el Dialog vía aria-labelledby.
 * El h2 semántico es el nivel correcto dentro de un diálogo (spec ARIA §6.3.5).
 */
const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-semibold text-cumple-primary-900 leading-tight', className)}
    {...props}
  />
));
ModalTitle.displayName = 'ModalTitle';

/**
 * ModalDescription — Texto descriptivo bajo el título.
 * Radix enlaza este elemento con el Dialog vía aria-describedby.
 * WCAG 1.3.1: la descripción contextual mejora la comprensión del AT.
 */
const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-cumple-gray-500 leading-5', className)}
    {...props}
  />
));
ModalDescription.displayName = 'ModalDescription';

/**
 * ModalFooter — Contenedor de acciones (botones).
 * Flex-row justify-end con gap-2. Borde top para separar del contenido.
 */
const ModalFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-row items-center justify-end gap-2 mt-6 pt-4 border-t border-cumple-gray-200',
        className,
      )}
      {...props}
    />
  ),
);
ModalFooter.displayName = 'ModalFooter';

/**
 * ModalClose — Botón de cierre programático expuesto para el footer o
 * cualquier otro lugar dentro del modal.
 * Usa asChild={true} para renderizarse como el elemento hijo (ej. un Button).
 *
 * @example
 *   <ModalFooter>
 *     <ModalClose asChild>
 *       <Button variant="secondary">Cancelar</Button>
 *     </ModalClose>
 *     <Button variant="primary" onClick={onConfirm}>Confirmar</Button>
 *   </ModalFooter>
 */
const ModalClose = DialogPrimitive.Close;
ModalClose.displayName = 'ModalClose';

export {
  Modal,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
  ModalClose,
  modalContentVariants,
};
